import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bookmark, Check, Pencil, Trash2, X } from 'lucide-react';
import { useToast } from '../../components/ToastProvider';
import {
  MAX_NAME_LENGTH,
  SAVE_ERROR_MESSAGES,
  deleteView,
  findViewByName,
  listSavedViews,
  normalizeViewName,
  renameView,
  saveView,
  stripPageParam,
} from './savedViews';

// "Views" control for a filtered list (doc-316 contracts; doc-356 TPRM requests
// and document reviews): save the filter set you are looking at under a name,
// then get it back tomorrow in one click.
//
// APPLYING A VIEW IS A NAVIGATION. The saved value is a querystring, and each
// host list already re-seeds its controls when the URL changes from outside
// (useUrlSyncedFilters → seedFromParams). So this component never touches the
// filter setters; it pushes a URL and lets the existing, tested path do the
// work. A PUSH rather than a replace, so browser Back returns you to the view you
// were on before. That is also the whole reason this control is only mounted on
// lists that HAVE useUrlSyncedFilters — without it there is no re-seed to
// navigate into, and the only alternative is the second apply path savedViews.js
// forbids.
//
// The current querystring comes from the router, not from re-encoding the filter
// values: each list's write effect owns its own omit-at-default rules (bare
// `filter=deadline`, sort omitted at updatedAt, page omitted at 1) and a second
// encoder here would drift from every one of them.
//
// `storageKey` is what keeps the three surfaces apart — one localStorage key per
// list, no sharing, no aggregation. Pass one of savedViews.js's SAVED_VIEW_KEYS.
export default function SavedViewsMenu({ storageKey, hasFilters }) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const toggleRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [views, setViews] = useState(() => listSavedViews(storageKey));
  // Sub-forms. At most one is ever active; resetForms() clears all of them.
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [overwriteTarget, setOverwriteTarget] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [formError, setFormError] = useState(null);

  const resetForms = useCallback(() => {
    setSaving(false);
    setSaveName('');
    setOverwriteTarget(null);
    setRenameId(null);
    setRenameName('');
    setDeleteId(null);
    setFormError(null);
  }, []);

  const hasSubForm = saving || renameId !== null || deleteId !== null;

  // Dismiss a sub-form and put focus somewhere known: on the toggle. Every
  // sub-form action ends by unmounting the control that was clicked, and React
  // REUSES those DOM nodes across the re-render — so "focus stayed inside the
  // menu" is not good enough. Measured: clicking X on the rename form left focus
  // sitting on that row's Delete button, because the same node came back as the
  // trash icon. Sending it to the toggle instead is boring and predictable.
  const dismissForms = useCallback(() => {
    resetForms();
    if (toggleRef.current) toggleRef.current.focus();
  }, [resetForms]);

  const closeMenu = useCallback((restoreFocus) => {
    setOpen(false);
    resetForms();
    // Escape and the like hand focus back to the anchor, so a keyboard user is
    // not dumped on <body> with their tab position lost. An outside click must
    // NOT do this — it would steal focus from whatever was just clicked.
    if (restoreFocus && toggleRef.current) toggleRef.current.focus();
  }, [resetForms]);

  // Re-read on open: another tab (or another Docket window) may have changed the
  // list since this one mounted. storageKey is in the deps for correctness, not
  // because it moves — each list passes one fixed key.
  useEffect(() => {
    if (open) setViews(listSavedViews(storageKey));
  }, [open, storageKey]);

  // KEEP FOCUS INSIDE THE MENU (doc-328). Escape no longer DEPENDS on this since
  // doc-360 moved it to a document listener, but a popover that silently drops
  // focus to <body> still loses a keyboard user their tab position, and Tab from
  // there restarts at the top of the page. Focus does drop, by default, in
  // three places: every sub-form action ends by unmounting the control that was
  // clicked (Cancel, Save, Delete, the trash icon that opened the confirm), which
  // drops document.activeElement to <body>; and on macOS Safari/Firefox clicking
  // the toggle does not focus it in the first place. One rule covers all of them:
  // whenever the panel's contents change, if nothing inside the menu holds focus,
  // hand it back to the toggle. Sub-forms with an autoFocus input already hold it,
  // so this leaves them alone rather than yanking the caret out of the name field.
  // (dismissForms below covers the same ground deterministically for the paths it
  // owns; this effect is the net for the ones it does not — opening the menu, and
  // anything a future edit adds.)
  //
  // The TOGGLE, not the panel: it is a real .btn, so it wears the design system's
  // own :focus-visible ring (components.css "Keyboard focus (author-owned)"),
  // whereas a focusable panel div would have shown Chrome's default blue outline
  // around the whole 300px popover — the per-browser UA improvisation that block
  // exists to replace. Tab still walks straight into the panel from here, since
  // the panel follows the toggle in DOM order.
  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    const toggle = toggleRef.current;
    if (!container || !toggle) return;
    if (container.contains(document.activeElement)) return;
    toggle.focus();
  }, [open, views, saving, overwriteTarget, renameId, deleteId]);

  // Close on an outside click. mousedown rather than click, so the menu is gone
  // before whatever was clicked reacts.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (containerRef.current && containerRef.current.contains(event.target)) return;
      closeMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, closeMenu]);

  // Escape closes exactly ONE thing: the open sub-form if there is one,
  // otherwise the menu.
  //
  // ON DOCUMENT, IN THE CAPTURE PHASE (doc-360). This used to be a React
  // onKeyDown on the wrapper below, which meant it only fired while focus was
  // INSIDE the menu. The two focus nets around it (doc-328's effect, doc-335's
  // onPanelMouseDown) both defend against focus falling out of the menu on its
  // own — but neither fires when an OUTSIDE listener STEALS focus without
  // changing any menu state. The `/` accelerator in CommandK.js does exactly
  // that: with a destructive "Delete X?" confirm up, focus is on a BUTTON, so
  // CommandK's own text-entry guard does not bail, and it moves focus to the
  // page search box. Escape was then dead and the confirm had no keyboard way
  // out. Listening on document instead of on the wrapper makes Escape survive
  // focus leaving, rather than trying (and eventually failing) to keep it in.
  //
  // CAPTURE + stopImmediatePropagation, and both halves are load-bearing:
  // doc-262 ("one Escape dismissed two layers at once") is why this handler
  // stops the event at all, and Modal.js:48,62 also listens for Escape on
  // `document`. stopPropagation() between two listeners on the SAME node does
  // NOTHING — only stopImmediatePropagation() does — and bubble-phase order
  // follows registration order, so a menu opened while a modal is already up
  // would fire second and be too late. Document-capture fires before every
  // bubble-phase document listener regardless of registration order, so exactly
  // one layer dismisses. Capture on document also sees keydowns originating
  // INSIDE the menu, so this one listener covers the in-menu case the wrapper
  // handler used to own.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDownCapture = (event) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      event.preventDefault();
      if (hasSubForm) dismissForms();
      else closeMenu(true);
    };
    document.addEventListener('keydown', onKeyDownCapture, true);
    return () => document.removeEventListener('keydown', onKeyDownCapture, true);
  }, [open, hasSubForm, dismissForms, closeMenu]);

  // KEEP FOCUS ON MOUSEDOWN TOO (doc-335). The two nets above only fire when the
  // panel's CONTENTS change, and clicking inert content inside the panel — the
  // muted footer line, the “Delete X?” label, the panel's own padding — changes
  // no state at all. Chrome's default action for that mousedown is to move focus
  // to the nearest focusable ancestor, which here is nothing, so activeElement
  // lands on <body>. Measured live at the time: a destructive delete confirm left
  // with no keyboard way out, because Escape was then a wrapper handler. doc-360
  // removed that dependency, so this guard now earns its keep for the same reason
  // the doc-328 net above does — not dumping a keyboard user on <body>.
  //
  // Preventing the blur beats refocusing after it — no flicker and no extra
  // render — and it composes with the document-level outside-click listener
  // above, which early-returns for in-container targets and is untouched.
  //
  // Gate on closest(), not on tagName: the rename and delete affordances are
  // <svg> icons INSIDE a <button>, and preventDefault-ing those would stop the
  // button taking focus on its own click. The cost is that the two lines of inert
  // prose in the panel can no longer be drag-selected; the view names can, since
  // they live in buttons.
  const onPanelMouseDown = (event) => {
    if (event.target.closest('button, a, input, select, textarea, [tabindex], [contenteditable]')) return;
    event.preventDefault();
  };

  const reportWriteFailure = (result) => {
    if (result.error === 'write-failed') {
      toast.error('Saved views', SAVE_ERROR_MESSAGES['write-failed']);
      return true;
    }
    return false;
  };

  const applyView = (view) => {
    // Focus back to the toggle: applying from the keyboard otherwise unmounts the
    // button you just pressed Enter on and leaves focus on <body>.
    closeMenu(true);
    // Applying the view you are already on is a no-op by construction — the
    // re-seed compares querystrings and ignores an identical one.
    navigate({ pathname: location.pathname, search: view.qs ? `?${view.qs}` : '' });
  };

  const startSave = () => {
    resetForms();
    setSaving(true);
  };

  // `force` arrives from the overwrite confirmation below.
  const submitSave = (force = false) => {
    const cleanName = normalizeViewName(saveName);
    if (!cleanName) {
      setFormError(SAVE_ERROR_MESSAGES['empty-name']);
      return;
    }
    const clash = findViewByName(views, cleanName);
    if (clash && !force) {
      // Never two views under one name, and never a silent overwrite of a
      // colleague's morning filter set either.
      setOverwriteTarget(clash);
      setFormError(null);
      return;
    }
    const result = saveView(storageKey, cleanName, stripPageParam(location.search));
    setViews(result.views);
    if (!result.ok) {
      if (!reportWriteFailure(result)) {
        setFormError(SAVE_ERROR_MESSAGES[result.error] || 'Could not save the view.');
      }
      setOverwriteTarget(null);
      return;
    }
    dismissForms();
    toast.success(clash ? 'View updated' : 'View saved', `“${cleanName}” is saved in this browser.`);
  };

  const startRename = (view) => {
    resetForms();
    setRenameId(view.id);
    setRenameName(view.name);
  };

  const submitRename = () => {
    const result = renameView(storageKey, renameId, renameName);
    setViews(result.views);
    if (!result.ok) {
      if (!reportWriteFailure(result)) {
        setFormError(SAVE_ERROR_MESSAGES[result.error] || 'Could not rename the view.');
      }
      return;
    }
    dismissForms();
  };

  const confirmDelete = (view) => {
    const result = deleteView(storageKey, view.id);
    setViews(result.views);
    if (!result.ok) {
      if (!reportWriteFailure(result)) {
        setFormError(SAVE_ERROR_MESSAGES[result.error] || 'Could not delete the view.');
      }
      return;
    }
    dismissForms();
    toast.success('View deleted', `“${view.name}” is no longer saved.`);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        ref={toggleRef}
        className={open ? 'btn sm toggled' : 'btn outline sm'}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => {
          resetForms();
          setOpen((wasOpen) => !wasOpen);
        }}
        style={{ whiteSpace: 'nowrap' }}
        title="Save the current filters as a named view, or open one you saved"
      >
        <Bookmark className="w-3.5 h-3.5" />
        Views
        {/* The count INHERITS the button's colour rather than wearing .muted:
            `.btn.toggled` swaps the surface to --surface-yellow, and a
            de-emphasised grey over a tinted surface is the contrast trap in
            GOTCHAS. */}
        {views.length > 0 && <span style={{ fontSize: 11 }}>{views.length}</span>}
      </button>

      {open && (
        <div
          className="card"
          aria-label="Saved views"
          onMouseDown={onPanelMouseDown}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 60,
            width: 300,
            padding: 8,
            textAlign: 'left',
            // .card carries no elevation of its own, so a floating panel needs
            // one or it reads as part of the page behind it (the same shadow
            // Combobox's menu uses).
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {views.length === 0 && !saving && (
            <div className="muted" style={{ fontSize: 12, padding: '6px 8px' }}>
              No saved views yet.
            </div>
          )}

          {views.map((view) => (
            <div key={view.id} style={{ padding: '2px 0' }}>
              {renameId === view.id ? (
                <div className="row" style={{ gap: 4, padding: '0 2px' }}>
                  <input
                    className="input"
                    aria-label="View name"
                    value={renameName}
                    maxLength={MAX_NAME_LENGTH}
                    autoFocus
                    onChange={(e) => { setRenameName(e.target.value); setFormError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitRename(); } }}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <button type="button" className="icon-btn" onClick={submitRename} aria-label="Save name">
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" className="icon-btn" onClick={dismissForms} aria-label="Cancel rename">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : deleteId === view.id ? (
                <div style={{ padding: '4px 6px' }}>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>Delete “{view.name}”?</div>
                  <div className="row" style={{ gap: 6 }}>
                    <button type="button" className="btn danger xs" onClick={() => confirmDelete(view)}>
                      Delete
                    </button>
                    <button type="button" className="btn ghost xs" onClick={dismissForms}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="row" style={{ gap: 2 }}>
                  <button
                    type="button"
                    className="btn ghost sm"
                    onClick={() => applyView(view)}
                    // overflow:hidden is load-bearing — .btn is nowrap, so the
                    // span's ellipsis only kicks in if the button clips.
                    style={{ flex: 1, minWidth: 0, justifyContent: 'flex-start', textAlign: 'left', overflow: 'hidden' }}
                    title={`Apply “${view.name}”`}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {view.name}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => startRename(view)}
                    aria-label={`Rename ${view.name}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => { dismissForms(); setDeleteId(view.id); }}
                    aria-label={`Delete ${view.name}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* Saving is only offered when something is actually filtered — a view
              over the unfiltered list is just the list. hasFilters comes from the
              hook, so this cannot disagree with the chip row about what counts as
              a filter. */}
          {hasFilters && !saving && (
            <div style={{ marginTop: views.length ? 6 : 0, paddingTop: views.length ? 6 : 0, borderTop: views.length ? 'var(--stroke-xs) solid var(--border-primary)' : 'none' }}>
              <button type="button" className="btn ghost sm" onClick={startSave} style={{ width: '100%', justifyContent: 'flex-start' }}>
                <Bookmark className="w-3.5 h-3.5" />
                Save current view
              </button>
            </div>
          )}

          {saving && (
            <div style={{ marginTop: views.length ? 6 : 0, paddingTop: views.length ? 6 : 0, borderTop: views.length ? 'var(--stroke-xs) solid var(--border-primary)' : 'none' }}>
              <div className="row" style={{ gap: 4, padding: '0 2px' }}>
                <input
                  className="input"
                  aria-label="New view name"
                  placeholder="Name this view"
                  value={saveName}
                  maxLength={MAX_NAME_LENGTH}
                  autoFocus
                  onChange={(e) => { setSaveName(e.target.value); setOverwriteTarget(null); setFormError(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSave(); } }}
                  style={{ flex: 1, minWidth: 0 }}
                />
                <button type="button" className="btn primary sm" onClick={() => submitSave()}>Save</button>
                <button type="button" className="icon-btn" onClick={dismissForms} aria-label="Cancel save">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {overwriteTarget && (
                <div style={{ padding: '6px 4px 0' }}>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>
                    “{overwriteTarget.name}” already exists. Replace its filters?
                  </div>
                  <div className="row" style={{ gap: 6 }}>
                    <button type="button" className="btn primary xs" onClick={() => submitSave(true)}>Replace</button>
                    <button type="button" className="btn ghost xs" onClick={() => setOverwriteTarget(null)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {formError && (
            <div className="field-error" style={{ padding: '6px 4px 0' }} role="alert">
              {formError}
            </div>
          )}

          {/* Said out loud rather than discovered on a second laptop: v1 is
              localStorage, so views are per-browser and do not sync. Shared /
              cross-device views are a separate, backend-shaped change. */}
          <div className="muted" style={{ fontSize: 11, padding: '8px 6px 2px' }}>
            Views are saved in this browser only.
          </div>
        </div>
      )}
    </div>
  );
}
